/**
 * The brief (decision 0049): Project.description, at most BRIEF_MAX_CHARS. `ReviseBrief.baseHash` is `briefHash` of the
 * text the edit was written against; the turn pack shows `briefHashShort` as the brief's version.
 */
import type { BriefRevision } from "@minga/protocol"
import { sha256Hex } from "./hash"
import type { ProjectEventEnvelope } from "./types"

export { BRIEF_MAX_CHARS } from "@minga/protocol"

/** The full version of a brief text: its SHA-256 (hex). */
export const briefHash = (text: string): string => sha256Hex(text)

/** The version a turn pack shows (12 hex characters); the tool layer expands it to the full hash. */
export const briefHashShort = (text: string): string => briefHash(text).slice(0, 12)

/**
 * The brief template's five sections (decision 0051; the web's template and the onboard-project skill name the same
 * five). Wave O1 (decision 0061): the fifth is "How we work" (build and test, branches and PRs, where it runs, "say what's
 * missing and stop"); briefs written with "How to build and test" keep their heading and count as having it.
 */
export const BRIEF_HEADINGS = ["Vision", "For whom", "Principles", "Not doing", "How we work"] as const
export type BriefHeading = (typeof BRIEF_HEADINGS)[number]

/** Older names of a template section; stored briefs are never rewritten. */
export const BRIEF_HEADING_ALIASES: Readonly<Partial<Record<BriefHeading, ReadonlyArray<string>>>> = {
  "How we work": ["How to build and test", "How to build"],
}

const headingKeyOf = (text: string): string =>
  text.replace(/\*\*/g, "").trim().replace(/\s+/g, " ").toLowerCase()

/**
 * The template section a heading text names (its own name or an alias; any case, bold markers and extra spaces
 * ignored), or null for a custom section: `briefHeadingKey("How to build and test") === "How we work"`.
 */
export const briefHeadingKey = (text: string): BriefHeading | null => {
  const key = headingKeyOf(text)
  for (const h of BRIEF_HEADINGS) {
    if (headingKeyOf(h) === key) return h
    if ((BRIEF_HEADING_ALIASES[h] ?? []).some((a) => headingKeyOf(a) === key)) return h
  }
  return null
}

const BRIEF_LINE_MAX = 200

const clipLine = (line: string): string =>
  line.length > BRIEF_LINE_MAX ? `${line.slice(0, BRIEF_LINE_MAX - 1).trimEnd()}…` : line

/** A markdown heading line: `#`–`######`, then its text. */
const HEADING = /^\s{0,3}(#{1,6})\s+(.*)$/

/** A line without its list marker (`-`, `*`) and bold markers (`**`), trimmed. */
const cleanLine = (line: string): string =>
  line
    .trim()
    .replace(/^[-*]\s+/, "")
    .replace(/\*\*/g, "")
    .trim()

type BriefSection = "vision" | "notDoing" | "other"

/** Which section a heading text opens, and the text after the section name on the same line. */
const sectionOf = (text: string): { readonly section: BriefSection; readonly rest: string } => {
  const t = text.replace(/\*\*/g, "").trim()
  const vision = /^vision\b\s*:?\s*(.*)$/i.exec(t)
  if (vision) return { section: "vision", rest: vision[1] ?? "" }
  const not = /^not\s+doing\b\s*:?\s*(.*)$/i.exec(t)
  if (not) return { section: "notDoing", rest: not[1] ?? "" }
  return { section: "other", rest: "" }
}

/**
 * The Overview's brief lines (decision 0051): the first non-empty line under `## Vision` and under `## Not doing` (text
 * on the heading line itself counts; headings `#`–`###`, any case; list and bold markers stripped; ≤ 200 characters).
 * `fallback`: when neither line is found (no such heading, or the sections are empty, e.g. a template with only Principles
 * filled in), the first two non-empty, non-heading lines (an old notes text, or what the brief does say); else [].
 */
export const briefLines = (
  text: string,
): {
  readonly vision: string | null
  readonly notDoing: string | null
  readonly fallback: ReadonlyArray<string>
} => {
  let vision: string | null = null
  let notDoing: string | null = null
  let foundVision = false
  let foundNotDoing = false
  let current: BriefSection | null = null
  const plain: string[] = []
  const take = (section: BriefSection | null, line: string) => {
    const clean = cleanLine(line)
    if (clean === "") return
    if (section === "vision" && vision === null) vision = clipLine(clean)
    else if (section === "notDoing" && notDoing === null) notDoing = clipLine(clean)
  }
  for (const line of text.split(/\r?\n/)) {
    const h = HEADING.exec(line)
    if (h) {
      const level = h[1]?.length ?? 0
      const { section, rest } = level <= 3 ? sectionOf(h[2] ?? "") : { section: "other" as const, rest: "" }
      // a section is read once: the first heading of its name
      current =
        (section === "vision" && foundVision) || (section === "notDoing" && foundNotDoing) ? "other" : section
      if (current === "vision") foundVision = true
      if (current === "notDoing") foundNotDoing = true
      take(current, rest)
      continue
    }
    take(current, line)
    if (plain.length < 2) {
      const clean = cleanLine(line)
      if (clean !== "") plain.push(clipLine(clean))
    }
  }
  return { vision, notDoing, fallback: vision === null && notDoing === null ? plain : [] }
}

/**
 * The brief's revisions from the project's event log (decision 0051), oldest first: `ProjectCreated` with a text
 * (rev 1), an old `ProjectUpdated` whose patch changed `description` (decision 0049 moved it to `BriefRevised`), and
 * every `BriefRevised`. Other events are skipped. Pure; the cloud reverses and slices.
 */
export const briefRevisions = (events: ReadonlyArray<ProjectEventEnvelope>): ReadonlyArray<BriefRevision> => {
  const out: BriefRevision[] = []
  let current = ""
  const push = (env: ProjectEventEnvelope, body: string, before: string, why: string | null) => {
    out.push({
      rev: out.length + 1,
      seq: env.seq,
      at: env.ts,
      by: env.actor,
      approvedBy: env.approvedBy?.by ?? null,
      body,
      before,
      why,
    })
    current = body
  }
  for (const env of [...events].sort((a, b) => a.seq - b.seq)) {
    const e = env.event
    if (e._tag === "ProjectCreated") {
      const text = e.project.description
      if (text !== "") push(env, text, "", null)
      else current = ""
    } else if (e._tag === "ProjectUpdated") {
      const d = (e.patch as Record<string, unknown>).description
      if (typeof d === "string" && d !== current) push(env, d, current, null)
    } else if (e._tag === "BriefRevised") push(env, e.body, e.before, e.why)
  }
  return out
}
